// astro vue will render empty page if import vue from 'vue'
// @ts-ignore
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import { computed, ref, provide, onMounted, onBeforeUnmount } from 'vue'
import {
	exchangeLocalPrice,
	getCurrencySymbolOriginal,
} from '@/assets/ts/currency'
import {
	fetchSettingSupportedCurrencies,
	fetchSettingWarehouses,
	fetchSettingInsurance,
	instanceProductSharing,
	instanceBuyandship,
} from '@/assets/ts/apiManagement'
import {
	productSharingData,
	setPostDetails,
	checkExpressCheckoutEnable,
	productSharingPostPerPage,
} from '@/legacy/ts/productSharing'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import useProductSharing from '@/legacy/ts/useProductSharing'
import useHashQuery from '@/legacy/ts/useHashQuery'
import useSnackBar from '@/legacy/ts/useSnackBar'
import BsSnackBar from '@/legacy/components/SnackBar.vue'
import BsDialogAction from '@/legacy/components/DialogAction.vue'
import ProductSharingPostCard from '@/legacy/components/ProductSharingPostCard.vue'
import { fetchPricingCurrencyBuyforyou } from '@/legacy/ts/buyforyou'
import goErrorPage from '@/legacy/ts/goErrorPage'
import hybridPage from '@/lib/hybridPage'
import { getMcrRootUrl } from '@/lib/siteBaseUrl'

hybridPage()

createApp({
	name: 'Product-Sharing-Post',
	components: {
		BsSnackBar,
		BsDialogAction,
		ProductSharingPostCard,
	},
	setup() {
		const { postCardTextInfo, postListTextInfo } = window
		const { isShow, messages, dismiss, addMessage } = useSnackBar()
		const { searchParameter } = useHashQuery()
		const { isUserVerified, PSUsername, connectPS, setToken, isHasPSProfile } =
			useProductSharing()

		// work around, since post detail page don't open loop dialog, we provide empty function here
		// provide('postItemClickProvide', () => {
		// 	window.location.href = getProductSharingUrl('post', { id: post.id })
		// })
		// provide('setPostListInfoProvide', () => {})

		provide('addMessage', addMessage)

		const isLoadingPostDetail = ref(true)
		const isShowDialogPostDetail = ref(false)
		const isShowPostContentBlock = ref(false)
		const postDetail: any = ref(null)
		const categoryMap: any = ref(null)
		const warehouseIdMap = ref(null)
		const interestsMap = ref(null)
		const supportedCurrencies = ref(null)
		const buyforyouSupportWarehouse = ref(null)
		const b4uRates = ref<Record<string, number> | null>(null)
		const bnsRates = ref<Awaited<
			ReturnType<typeof fetchSettingInsurance>
		> | null>(null)
		const currencySymbolOriginal = ref('')
		const priceLocal = ref('')
		const priceLocalPost = ref('')
		const localCurrencyInfo = ref<Awaited<
			ReturnType<typeof fetchPricingCurrencyBuyforyou>
		> | null>(null)
		const currencyData = ref<Awaited<
			ReturnType<typeof fetchPricingCurrencyBuyforyou>
		> | null>(null)
		const isExpressCheckoutEnable = checkExpressCheckoutEnable()

		const postId = computed(() => {
			if (!import.meta.env.PROD) {
				// use search parameter in local environment
				const id = searchParameter.value.find(query => query.key === 'id')
				return id === undefined ? '' : id.val
			}
			const re = /\/sharing\/posts\//g
			return window.location.pathname
				.replace(/^\/en/, '')
				.replace(re, '')
				.replace('/', '')
		})

		const locale = computed(() => (import.meta.env.PROD ? '' : 'zh_TW'))
		const priceAfterExchange = computed(() => {
			if (!postDetail.value) {
				return ''
			}
			if (postDetail.value.itemInfo && b4uRates.value) {
				const localPrice =
					Number(postDetail.value.itemInfo.unitPrice) *
					b4uRates.value[postDetail.value.itemInfo.warehouse]
				return `${
					window.BUYANDSHIP.settings.current_country2.currency_symbol
				} ${Math.round(localPrice).toLocaleString('en')}`
			}
			return ''
		})
		const isProductSupportExpressCheckout = computed(() => {
			return (
				(postDetail.value &&
					postDetail.value.isExpressCheckout &&
					isExpressCheckoutEnable) ||
				false
			)
		})

		async function fetchPostDetails() {
			try {
				const {
					data: { data },
				} = await instanceProductSharing.get(`/posts/${postId.value}`)
				if (data.user.username === PSUsername.value) {
					window.location.href = `${getMcrRootUrl()}/account/productsharing/posts/${
						data.id
					}`
					return
				}
				postDetail.value = setPostDetails(data)

				createGAEvent(GaGeneralEvent.PageLoad, {
					page_name: GaGeneralPageName.CommunityPostDetailPage,
					page_object_id: `post_${postId.value}`,
					post_id: postId.value,
					product_id: postDetail.value.productSlug,
					is_express_checkout: postDetail.value.isExpressCheckout,
				})
			} catch (error: any) {
				if (error.response && error.response.status === 404) {
					goErrorPage()
				}
				if (error.response && error.response.status === 401) {
					connectPS().then(() => fetchPostDetails())
				}
			}
		}

		function fetchSettingShipmentCategory() {
			if (categoryMap.value) return Promise.resolve()
			return new Promise((resolve, reject) => {
				instanceBuyandship
					.get(`settings/${locale.value}?k[]=shipment_categories`)
					.then(({ data }) => {
						const list = data.shipment_categories.map((v: any) => v[2])
						categoryMap.value = [].concat(...list)
						resolve(true)
					})
					.catch(reject)
			})
		}

		function fetchSettingInterest() {
			return new Promise((resolve, reject) => {
				instanceBuyandship
					.get(`settings/${locale.value}?k[]=shipment_interests`)
					.then(({ data }) => {
						interestsMap.value = data.shipment_interests
						resolve(true)
					})
					.catch(reject)
			})
		}

		function fetchSettingBuyforyou() {
			return new Promise((resolve, reject) => {
				instanceBuyandship
					.get(`settings/${locale.value}?k[]=buyforyou`)
					.then(({ data }) => {
						const buyforyouWarehouses =
							(data.buyforyou && data.buyforyou.request_warehouses) || {}
						buyforyouSupportWarehouse.value = Object.keys(
							buyforyouWarehouses,
						).reduce((acc: any, curr: any) => {
							if (buyforyouWarehouses[curr] === 'yes') acc.push(curr)
							return acc
						}, [])
						resolve(true)
					})
					.catch(reject)
			})
		}

		function setPostActions(payload: any) {
			if (!payload || !postDetail.value || payload.id !== postDetail.value.id)
				return
			postDetail.value[payload.param] = payload.val
			if (payload.param === 'isLiked') {
				postDetail.value.likeCount = payload.likeCount
			}
		}

		async function setSupportedCurrencies() {
			supportedCurrencies.value = await fetchSettingSupportedCurrencies()
		}

		async function setWarehouseIdMap() {
			warehouseIdMap.value = await fetchSettingWarehouses()
		}

		async function fetchCurrencyData() {
			try {
				currencyData.value = await fetchPricingCurrencyBuyforyou()
			} catch (error) {
				currencyData.value = null
			}
		}

		async function fetchBNSRates() {
			bnsRates.value = await fetchSettingInsurance()
		}

		function setB4URates() {
			if (!currencyData.value) return

			// Transform array to b4uRates object, keeping first occurrence of duplicate keys like APP's behavior
			b4uRates.value = currencyData.value.reduce(
				(acc: { [key: string]: number }, item: any) => {
					if (!acc[item.key]) {
						acc[item.key] = item.rate
					}
					return acc
				},
				{},
			)
		}

		async function setCurrencySymbolOriginal() {
			currencySymbolOriginal.value = getCurrencySymbolOriginal({
				warehouse: postDetail.value.itemInfo.warehouse,
				warehouseIdMap: warehouseIdMap.value,
				supportedCurrencies: supportedCurrencies.value,
			})
		}

		async function setPriceLocal() {
			const priceOriginal = postDetail.value.isExpressCheckout
				? postDetail.value.itemInfo.ecPrice
				: postDetail.value.itemInfo.unitPrice

			const hasB4uRate =
				b4uRates.value?.[postDetail.value.itemInfo.warehouse] != null
			const rates = hasB4uRate ? b4uRates.value : bnsRates.value
			const floatMax = hasB4uRate
				? currencyData.value?.find(
						(item: any) => item.key === postDetail.value.itemInfo.warehouse,
				  )?.currencyDecimal || 0
				: 0

			priceLocal.value = `≈ ${exchangeLocalPrice({
				rates,
				price: priceOriginal,
				warehouse: postDetail.value.itemInfo.warehouse,
				count: 1,
			})}`
			priceLocalPost.value = `≈ ${exchangeLocalPrice({
				rates,
				price: postDetail.value.itemInfo.unitPrice,
				warehouse: postDetail.value.itemInfo.warehouse,
				count: 1,
				floatMax,
			})}`
		}

		function setPricingInfo() {
			localCurrencyInfo.value = currencyData.value
		}

		function handlePopstate() {
			window.location.reload()
		}

		onMounted(async () => {
			if (postId.value) {
				if (productSharingData.isLogin && isUserVerified) {
					await setToken()
				}
				await Promise.all([
					fetchSettingShipmentCategory(),
					fetchSettingInterest(),
					fetchSettingBuyforyou(),
					setWarehouseIdMap(),
					setSupportedCurrencies(),
					fetchCurrencyData(),
					fetchBNSRates(),
					fetchPostDetails(),
					isHasPSProfile(),
				])

				setB4URates()
				if (isProductSupportExpressCheckout) {
					setPricingInfo()
				}
				await setPriceLocal()
				setCurrencySymbolOriginal()
				isLoadingPostDetail.value = false
			} else {
				goErrorPage()
			}

			window.addEventListener('popstate', handlePopstate)
		})

		onBeforeUnmount(() => {
			window.removeEventListener('popstate', handlePopstate)
		})

		return {
			productSharingPostPerPage,
			postCardTextInfo,
			postListTextInfo,
			isShow,
			messages,
			categoryMap,
			warehouseIdMap,
			interestsMap,
			supportedCurrencies,
			buyforyouSupportWarehouse,
			priceLocal,
			priceLocalPost,
			isShowDialogPostDetail,
			isLoadingPostDetail,
			localCurrencyInfo,
			isShowPostContentBlock,
			postDetail,
			dismiss,
			setPostActions,
		}
	},
}).mount('#bsMain')
